import {
    Avatar,
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { SwipeableList } from "react-swipeable-list";

export const CommentTimestampContainer = styled("div")({
    margin: "8px 0px 8px 40px",
    display: "flex",
    justifyContent: "center",
});

export const CommentTimestampChip = styled(Chip)({
    height: "20px",
    lineHeight: "20px",
    color: "var(--community-color-secondary-text)",
    fontSize: 14,
    backgroundColor: "transparent",
});

export const CommentContainerGrid = styled(Stack)({
    display: "flex",
    flex: 1,
});

export const CommentAvatarGrid = styled(Grid)({
    justifyContent: "right",
    alignItems: "start",
    flexWrap: "nowrap",
    display: "flex",
});

export const CommentAvatar = styled(Avatar, {
    shouldForwardProp: (prop) => prop !== "backgroundColor",
})<{ backgroundColor?: string }>(({ backgroundColor }) => ({
    backgroundColor:
        backgroundColor ?? "var(--community-avatar-background-color)",
    width: "32px",
    height: "32px",
    margin: "0 12px 0 0",
    textTransform: "uppercase",
}));

export const CommentContentContainerGrid = styled(Stack)({
    flexWrap: "nowrap",
    display: "grid",
    width: "100%",
    maxWidth: "-webkit-fill-available",
});

export const SwipeableListStyled = styled(SwipeableList)({
    width: "100%",
});

export const CommentContentPaper = styled(Box)({
    boxShadow: "none",
    backgroundColor: "var(--community-color-transparent-black-05)",
    padding: "4px",
    borderRadius: "8px",
    minWidth: "0px",
});

export const CommentHeaderLeftSide = styled(Box)({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
});

export const CommentUserNameTypography = styled(Box)({
    margin: "0",
    textAlign: "left",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
});

export const TrustNoteTypography = styled(Typography)({
    fontSize: "12px",
    fontWeight: 400,
    color: "var(--community-color-secondary-text)",
    lineHeight: "20px",
    marginTop: "8px",
});

export const CommentImageContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isOneImage",
})<{
    isOneImage?: boolean;
}>(({ isOneImage = false }) => ({
    textAlign: "left",
    display: "flex",
    justifyContent: "center",
    margin: "8px 0 0 0",
    ".grid-container": {
        padding: 0,
        ".border": {
            border: "none",
        },
    },
    img: {
        objectFit: "cover",
        ...(isOneImage
            ? {
                  maxHeight: "80vh",
              }
            : {
                  aspectRatio: "2/1",
                  "@media (max-width: 390px)": {
                      aspectRatio: "1",
                  },
              }),
    },
}));

export const CommentTextContentGrid = styled(Grid)({
    lineHeight: 1,
    paddingRight: "0px",
    width: "100%",
});

export const CommentTextTypography = styled(Box, {
    shouldForwardProp: (propName) => propName !== "showBigText",
})<{ showBigText: boolean }>(({ showBigText }) => ({
    margin: "8px 0 0 0",
    "& p": {
        lineHeight: showBigText ? "55px" : "20px",
        fontSize: showBigText ? "48px" : "14px",
    },
    width: "100%",
    whiteSpace: "pre-wrap",
}));

export const PreviewCommentTextTypography = styled(Box, {
    shouldForwardProp: (propName) => propName !== "showBigText",
})<{ showBigText: boolean }>(({ showBigText }) => ({
    margin: "8px 0 0 0",
    "& p": {
        lineHeight: showBigText ? "55px" : "20px",
        fontSize: showBigText ? "48px" : "14px",
    },
}));

export const CommentDateTypography = styled("span")({
    color: "var(--community-color-secondary-text)",
    fontSize: "12px",
});

export const CommentHeaderContainer = styled(Box)({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
});
export const CommentHeaderRightSide = styled(Box)({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "4px",
    marginLeft: "auto",
});
export const StyledSmall = styled("small")({
    color: "var(--community-color-secondary-text)",
    fontSize: "12px",
});
export const CommentOptionsButton = styled(IconButton)({
    width: "21px",
    height: "21px",
    svg: { fontSize: "16px" },
});

export const StyledTextActionButton = styled(Button)({
    fontSize: "14px",
    color: "var(--community-color-primary-text)",
    fontWeight: "500",
    border: "unset",
    alignItems: "center",
    lineHeight: "1.75",
    height: "32px",
    padding: "0",
    borderRadius: "90px !important",
    textTransform: "none",
    minWidth: "unset",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

export const HighlightedBox = styled(Box)({
    backgroundColor: "var(--community-color-light-steal-blue)",
    padding: "6px 8px 6px 8px",
    width: "24px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    margin: "-4px 4px 0 4px",
});

export const StyledTextActionWithBGColorButton = styled(StyledTextActionButton)(
    {
        backgroundColor: "var(--community-action-buttons-background-color)",
        "&:hover": {
            backgroundColor: "var(--community-action-buttons-background-color)",
        },
        padding: "0 8px",
    },
);

export const CommentContentContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: "0px",
});

export const CommentActionsBox = styled(Box)({
    display: "flex",
    alignItems: "center",
});

export const CommentReactionsGrid = styled(Grid)({
    display: "flex",
    alignItems: "center",
    marginRight: "12px",
});

export const CommentReactionsInnerGrid = styled(Grid)({
    display: "flex",
});

export const CommentReactionsBox = styled(Box)({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    button: {
        marginLeft: "12px",
    },
    "button:first-of-type": {
        marginLeft: "0",
    },
    position: "relative",
});

export const LinkoutButton = styled(Button)({
    width: "100%",
    backgroundColor: "var(--community-color-transparent-light-steal-blue-10)",
    "&:hover": {
        backgroundColor:
            "var(--community-color-transparent-light-steal-blue-20)",
    },
    marginTop: "8px",
    borderRadius: "4px",
    height: "40px",
    fontSize: "14px",
    color: "var(--community-color-light-steal-blue)",
    display: "flex",
    justifyContent: "flex-start",
    textTransform: "unset",
    lineHeight: "20px",
    paddingLeft: 0,
});
